/* eslint-disable @typescript-eslint/no-explicit-any -- U2000Input* / combobox génériques */
import { useMemo } from 'react';
import U2000InputText from '../../../../u2000Core/components/formControls/input/U2000InputText';
import U2000CheckBox from '../../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000InvalidFeedback from '../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback';
import U2000ComboBox from '../../../../u2000Core/components/formControls/combobox/U2000ComboBox';
import type { TFunction } from 'i18next';
import { NS } from '../../../U3153';
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import {
  U3153_INVOICE_TYPE_NATURES,
  u3153IsSystemInvoiceTypeCode,
  type U3153InvoiceTypeFormRow,
  type U3153InvoiceTypeNature,
  type U3153InvoiceTypeTranslationFormRow,
} from '../../../U3153Service';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';

type U3153LanguageOption = { value: string; label: string };
type U3153NatureOption = { value: U3153InvoiceTypeNature; label: string };

const COMBO_TRIGGER_HEIGHT = '[&_button]:box-border! [&_button]:h-[46px]! [&_button]:min-h-[46px]! [&_button]:max-h-[46px]! [&_button]:py-2.5! [&_button]:leading-snug!';

interface U3153InvoiceTypeBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3153InvoiceTypeFormRow>;
  t: TFunction;
}

function translationFieldKey(index: number, field: 'languageCode' | 'invoiceTypeLabel'): string {
  return `translations.${index}.${field}`;
}

function U3153InvoiceTypeBody({ formStateHandlers, t }: U3153InvoiceTypeBodyProps) {
  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const editMode = formStateHandlers.getFormEditMode();
  const codeReadonly = readonly || editMode !== U2000FormStateEnumEditMode.ADD;
  const natureReadonly =
    readonly || (editMode !== U2000FormStateEnumEditMode.ADD && u3153IsSystemInvoiceTypeCode(data?.code));

  const languageOptions = useMemo<U3153LanguageOption[]>(
    () => [
      { value: 'fr', label: t('lblLangFR', { ns: NS }) },
      { value: 'en', label: t('lblLangEN', { ns: NS }) },
    ],
    [t],
  );

  const natureOptions = useMemo<U3153NatureOption[]>(
    () =>
      U3153_INVOICE_TYPE_NATURES.map((value) => ({
        value,
        label: t(
          value === 'STANDARD'
            ? 'lblNatureStandard'
            : value === 'CREDIT'
              ? 'lblNatureCredit'
              : value === 'ADVANCE'
                ? 'lblNatureAdvance'
                : 'lblNatureWipWriteOff',
          { ns: NS },
        ),
      })),
    [t],
  );

  if (!data) return null;

  const setTranslations = (next: U3153InvoiceTypeTranslationFormRow[]) => {
    formStateHandlers.setData({ ...data, translations: next });
  };

  const addTranslationRow = () => {
    if (data.translations.length >= 2) return;
    const used = new Set(data.translations.map((tr) => tr.languageCode?.trim().toLowerCase()));
    const nextLang = !used.has('fr') ? 'fr' : !used.has('en') ? 'en' : '';
    setTranslations([...data.translations, { languageCode: nextLang, invoiceTypeLabel: '' }]);
  };

  const removeTranslationRow = (index: number) => {
    if (data.translations.length <= 1) return;
    setTranslations(data.translations.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <U2000InputText
          formStateHandlers={formStateHandlers as any}
          fieldName="code"
          label={t('lblInvoiceTypeCode', { ns: NS })}
          required
          maxLength={10}
          readonly={codeReadonly}
          inlineEnd={
            <U2000CheckBox
              formStateHandlers={formStateHandlers as any}
              fieldName="active"
              label={t('lblActive', { ns: NS })}
              readonly={readonly}
            />
          }
        />
        <div className={`min-w-0 ${COMBO_TRIGGER_HEIGHT}`}>
          <U2000ComboBox<U3153NatureOption>
            formStateHandlers={formStateHandlers as any}
            fieldName="nature"
            label={t('lblNature', { ns: NS })}
            data={natureOptions}
            keyField="value"
            descField="label"
            getValue={(item) => item.value}
            findItemByValue={(value, arr) => arr.find((o) => o.value === value) ?? null}
            formatLabel={(item) => item.label}
            readonly={natureReadonly}
            required
          />
        </div>
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{t('lblTranslationsSection', { ns: NS })}</h3>
            <U2000InvalidFeedback formStateHandlers={formStateHandlers as any} fieldName="translations" />
          </div>
          {!readonly && data.translations.length < 2 && (
            <U2000ActionBtn
              label={t('btnAddTranslation', { ns: NS })}
              handleClick={addTranslationRow}
              icon={FaPlus}
              hasRole={true}
            />
          )}
        </div>

        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblLanguageCode', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblInvoiceTypeLabel', { ns: NS })}</th>
                {!readonly && <th className="w-14 px-2 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {data.translations.map((row, index) => (
                <tr key={`tr-${index}-${row.id ?? 'new'}`}>
                  <td className="min-w-[200px] px-3 py-2 align-top">
                    <div className={`min-w-0 ${COMBO_TRIGGER_HEIGHT}`}>
                      <U2000ComboBox<U3153LanguageOption>
                        formStateHandlers={formStateHandlers as any}
                        fieldName={translationFieldKey(index, 'languageCode')}
                        label=""
                        data={languageOptions}
                        keyField="value"
                        descField="label"
                        getValue={(item) => item.value}
                        findItemByValue={(value, arr) => arr.find((o) => o.value === value) ?? null}
                        formatLabel={(item) => item.label}
                        readonly={readonly}
                        required
                      />
                    </div>
                  </td>
                  <td className="min-w-[220px] px-3 py-2 align-top">
                    <U2000InputText
                      formStateHandlers={formStateHandlers as any}
                      fieldName={translationFieldKey(index, 'invoiceTypeLabel')}
                      label={t('lblInvoiceTypeLabel', { ns: NS })}
                      hideLabel
                      required
                      maxLength={255}
                      readonly={readonly}
                    />
                    <U2000InvalidFeedback formStateHandlers={formStateHandlers as any} fieldName={`translations.${index}.version`} />
                  </td>
                  {!readonly && (
                    <td className="px-2 py-2 align-middle text-center">
                      <button
                        type="button"
                        className="inline-flex rounded-sm p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        onClick={() => removeTranslationRow(index)}
                        disabled={data.translations.length <= 1}
                        title={t('btnRemoveTranslation', { ns: NS })}
                        aria-label={t('btnRemoveTranslation', { ns: NS })}
                      >
                        <FaTrashAlt className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default U3153InvoiceTypeBody;
